import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TimeoutError, finalize, retry, timeout } from 'rxjs';
import {
  CreateVehicleProfileRequest,
  InitialPartMileageRequest,
  VehicleCatalogMatchResponse,
  VehicleProfile
} from './vehicle-profiles.model';
import { VehicleProfilesService } from './vehicle-profiles.service';
import { API_BASE_URL } from '../core/api-base-url';

type VehicleImageType = 'sedan' | 'suv' | 'hatch';

interface VehicleModelOption {
  make: string;
  model: string;
  year: number;
  trim?: string;
  imageType: VehicleImageType;
}

@Component({
  selector: 'app-vehicle-profiles',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './vehicle-profiles.view.html',
  styleUrls: ['./vehicle-profiles.view.css']
})
export class VehicleProfilesComponent implements OnInit, OnDestroy {
  private readonly requestTimeoutMs = 30000;
  private readonly vinMatchTimeoutMs = 7000;
  private readonly matchUiGuardPaddingMs = 1500;
  private readonly loadGuardPaddingMs = 2000;
  private readonly minVehicleYear = 1886;
  private readonly maxVehicleYear = new Date().getFullYear() + 1;
  private loadGuardTimer: ReturnType<typeof setTimeout> | null = null;
  private matchUiGuardTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  ownerId = '';
  viewMode: 'profiles' | 'add' = 'profiles';
  readonly makeOptions = [
    'Audi',
    'BMW',
    'Chevrolet',
    'Ford',
    'Dodge',
    'GMC',
    'Honda',
    'Hyundai',
    'Jeep',
    'Kia',
    'Lexus',
    'Mercedes-Benz',
    'Nissan',
    'Ram',
    'Subaru',
    'Mazda',
    'Tesla',
    'Toyota',
    'Volkswagen'
  ];
  readonly makeModelCatalog: Record<string, VehicleModelOption[]> = {
    Audi: [
      { make: 'Audi', model: 'A4', year: 2020, trim: 'Sedan', imageType: 'sedan' },
      { make: 'Audi', model: 'Q5', year: 2021, trim: 'SUV', imageType: 'suv' },
      { make: 'Audi', model: 'A3', year: 2019, trim: 'Sedan', imageType: 'hatch' }
    ],
    BMW: [
      { make: 'BMW', model: '330i', year: 2020, trim: 'Sedan', imageType: 'sedan' },
      { make: 'BMW', model: 'X3', year: 2021, trim: 'SUV', imageType: 'suv' },
      { make: 'BMW', model: 'X1', year: 2019, trim: 'SUV', imageType: 'hatch' }
    ],
    Chevrolet: [
      { make: 'Chevrolet', model: 'Malibu', year: 2020, trim: 'Sedan', imageType: 'sedan' },
      { make: 'Chevrolet', model: 'Equinox', year: 2020, trim: 'SUV', imageType: 'suv' },
      { make: 'Chevrolet', model: 'Spark', year: 2019, trim: 'Hatchback', imageType: 'hatch' }
    ],
    Ford: [
      { make: 'Ford', model: 'Fusion', year: 2020, trim: 'Sedan', imageType: 'sedan' },
      { make: 'Ford', model: 'Escape', year: 2021, trim: 'SUV', imageType: 'suv' },
      { make: 'Ford', model: 'Focus', year: 2018, trim: 'Hatchback', imageType: 'hatch' }
    ],
    Dodge: [
      { make: 'Dodge', model: 'Charger', year: 2020, trim: 'Sedan', imageType: 'sedan' },
      { make: 'Dodge', model: 'Durango', year: 2021, trim: 'SUV', imageType: 'suv' },
      { make: 'Dodge', model: 'Caliber', year: 2012, trim: 'Hatchback', imageType: 'hatch' }
    ],
    GMC: [
      { make: 'GMC', model: 'Terrain', year: 2021, trim: 'SUV', imageType: 'suv' },
      { make: 'GMC', model: 'Acadia', year: 2020, trim: 'SUV', imageType: 'suv' },
      { make: 'GMC', model: 'Yukon', year: 2019, trim: 'SUV', imageType: 'suv' }
    ],
    Honda: [
      { make: 'Honda', model: 'Civic', year: 2020, trim: 'Touring Sedan', imageType: 'hatch' },
      { make: 'Honda', model: 'Accord', year: 2020, trim: 'Sedan', imageType: 'sedan' },
      { make: 'Honda', model: 'CR-V', year: 2020, trim: 'S', imageType: 'suv' }
    ],
    Hyundai: [
      { make: 'Hyundai', model: 'Elantra', year: 2020, trim: 'Sedan', imageType: 'hatch' },
      { make: 'Hyundai', model: 'Sonata', year: 2021, trim: 'Sedan', imageType: 'sedan' },
      { make: 'Hyundai', model: 'Tucson', year: 2020, trim: 'SUV', imageType: 'suv' }
    ],
    Jeep: [
      { make: 'Jeep', model: 'Compass', year: 2020, trim: 'SUV', imageType: 'suv' },
      { make: 'Jeep', model: 'Cherokee', year: 2020, trim: 'SUV', imageType: 'suv' },
      { make: 'Jeep', model: 'Renegade', year: 2019, trim: 'SUV', imageType: 'hatch' }
    ],
    Kia: [
      { make: 'Kia', model: 'Forte', year: 2020, trim: 'Sedan', imageType: 'sedan' },
      { make: 'Kia', model: 'Sportage', year: 2021, trim: 'SUV', imageType: 'suv' },
      { make: 'Kia', model: 'Soul', year: 2020, trim: 'Hatchback', imageType: 'hatch' }
    ],
    Lexus: [
      { make: 'Lexus', model: 'ES', year: 2020, trim: 'Sedan', imageType: 'sedan' },
      { make: 'Lexus', model: 'RX', year: 2020, trim: 'SUV', imageType: 'suv' },
      { make: 'Lexus', model: 'NX', year: 2021, trim: 'SUV', imageType: 'suv' }
    ],
    'Mercedes-Benz': [
      { make: 'Mercedes-Benz', model: 'C300', year: 2020, trim: 'Sedan', imageType: 'sedan' },
      { make: 'Mercedes-Benz', model: 'GLC', year: 2021, trim: 'SUV', imageType: 'suv' },
      { make: 'Mercedes-Benz', model: 'A220', year: 2019, trim: 'Sedan', imageType: 'hatch' }
    ],
    Nissan: [
      { make: 'Nissan', model: 'Sentra', year: 2020, trim: 'Sedan', imageType: 'sedan' },
      { make: 'Nissan', model: 'Altima', year: 2020, trim: 'Sedan', imageType: 'hatch' },
      { make: 'Nissan', model: 'Rogue', year: 2021, trim: 'SUV', imageType: 'suv' }
    ],
    Ram: [
      { make: 'Ram', model: '1500', year: 2020, trim: 'Truck', imageType: 'suv' },
      { make: 'Ram', model: '2500', year: 2021, trim: 'Truck', imageType: 'suv' },
      { make: 'Ram', model: 'ProMaster City', year: 2019, trim: 'Van', imageType: 'hatch' }
    ],
    Subaru: [
      { make: 'Subaru', model: 'Impreza', year: 2020, trim: 'Sedan', imageType: 'hatch' },
      { make: 'Subaru', model: 'Outback', year: 2020, trim: 'SUV', imageType: 'suv' },
      { make: 'Subaru', model: 'Legacy', year: 2021, trim: 'Sedan', imageType: 'sedan' }
    ],
    Mazda: [
      { make: 'Mazda', model: 'Mazda3', year: 2020, trim: 'Sedan', imageType: 'hatch' },
      { make: 'Mazda', model: 'CX-5', year: 2021, trim: 'SUV', imageType: 'suv' },
      { make: 'Mazda', model: 'Mazda6', year: 2020, trim: 'Sedan', imageType: 'sedan' }
    ],
    Tesla: [
      { make: 'Tesla', model: 'Model 3', year: 2021, trim: 'Sedan', imageType: 'hatch' },
      { make: 'Tesla', model: 'Model Y', year: 2021, trim: 'SUV', imageType: 'suv' },
      { make: 'Tesla', model: 'Model S', year: 2020, trim: 'Sedan', imageType: 'sedan' }
    ],
    Toyota: [
      { make: 'Toyota', model: 'Corolla', year: 2020, trim: 'Sedan', imageType: 'hatch' },
      { make: 'Toyota', model: 'Camry', year: 2020, trim: 'Sedan', imageType: 'sedan' },
      { make: 'Toyota', model: 'RAV4', year: 2021, trim: 'SUV', imageType: 'suv' }
    ],
    Volkswagen: [
      { make: 'Volkswagen', model: 'Jetta', year: 2020, trim: 'Sedan', imageType: 'sedan' },
      { make: 'Volkswagen', model: 'Golf', year: 2020, trim: 'Hatchback', imageType: 'hatch' },
      { make: 'Volkswagen', model: 'Tiguan', year: 2021, trim: 'SUV', imageType: 'suv' }
    ]
  };
  vehicles: VehicleProfile[] = [];
  selectedVehicleId: number | null = null;
  selectedMakeCandidate = '';
  selectedModelCandidate: VehicleModelOption | null = null;
  loading = false;
  saving = false;
  deleting = false;
  matchingVehicle = false;
  errorMessage = '';
  vehicleMatchMessage = '';

  form = {
    vin: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    currentOdometerKm: 0,
    oilFilterKm: 0,
    engineOilKm: 0,
    sparkPlugsKm: 0,
    brakePadsKm: 0,
    cabinAirFilterKm: 0,
    engineAirFilterKm: 0,
    fuelFilterKm: 0
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly vehicleProfilesApi: VehicleProfilesService,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone
  ) {}

  ngOnInit(): void {
    const ownerFromQuery = (this.route.snapshot.queryParamMap.get('ownerId') ?? '').trim();
    const ownerFromStorage = this.getSavedOwnerId();
    this.ownerId = ownerFromQuery || ownerFromStorage || '';

    if (!this.ownerId) {
      this.router.navigate(['/login']);
      return;
    }

    this.persistOwnerId(this.ownerId);

    const selectedRaw = this.route.snapshot.queryParamMap.get('selectedVehicleId');
    const selectedParsed = selectedRaw ? Number.parseInt(selectedRaw, 10) : Number.NaN;
    const preferredVehicleId = Number.isFinite(selectedParsed) ? selectedParsed : null;

    this.loadVehicles(preferredVehicleId);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearLoadGuard();
    this.clearMatchUiGuard();
  }

  openAddVehicle(): void {
    this.viewMode = 'add';
    this.selectedMakeCandidate = '';
    this.selectedModelCandidate = null;
    this.resetAddFormValues();
    this.errorMessage = '';
    this.vehicleMatchMessage = '';
    this.syncUi();
  }

  backToProfiles(): void {
    this.viewMode = 'profiles';
    this.errorMessage = '';
    this.syncUi();
  }

  backToDashboard(): void {
    const queryParams: Record<string, string | number> = {
      ownerId: this.ownerId
    };

    if (this.selectedVehicleId !== null) {
      queryParams['vehicleId'] = this.selectedVehicleId;
    }

    this.router.navigate(['/dashboard'], { queryParams });
  }

  selectMake(make: string): void {
    this.selectedMakeCandidate = make;
    this.vehicleMatchMessage = '';
    this.errorMessage = '';
    this.syncUi();
  }

  confirmMakeSelection(): void {
    if (this.selectedMakeCandidate) {
      this.form.make = this.selectedMakeCandidate;
      this.form.model = '';
      this.selectedModelCandidate = null;
      this.vehicleMatchMessage = '';
      this.errorMessage = '';
      this.syncUi();
      return;
    }

    this.errorMessage = 'Select a make to continue.';
  }

  selectModelOption(option: VehicleModelOption): void {
    this.selectedModelCandidate = option;
    this.errorMessage = '';
    this.vehicleMatchMessage = '';
    this.syncUi();
  }

  confirmModelSelection(): void {
    if (this.selectedModelCandidate !== null) {
      this.form.model = this.selectedModelCandidate.model;
      this.form.year = this.selectedModelCandidate.year;
      this.errorMessage = '';
      this.syncUi();
      return;
    }

    this.errorMessage = 'Select a model to continue.';
  }

  restartMakeSelection(): void {
    this.form.make = '';
    this.form.model = '';
    this.selectedMakeCandidate = '';
    this.selectedModelCandidate = null;
    this.errorMessage = '';
    this.vehicleMatchMessage = '';
    this.syncUi();
  }

  restartModelSelection(): void {
    this.form.model = '';
    this.selectedModelCandidate = null;
    this.errorMessage = '';
    this.vehicleMatchMessage = '';
    this.syncUi();
  }

  backFromAddFlow(): void {
    if (this.form.model.trim().length > 0) {
      this.restartModelSelection();
      return;
    }

    if (this.form.make.trim().length > 0 || this.selectedMakeCandidate.trim().length > 0) {
      this.restartMakeSelection();
      return;
    }

    this.backToProfiles();
  }

  matchVehicleFromInput(): void {
    const vin = this.form.vin.trim();
    const make = this.form.make.trim();
    const model = this.form.model.trim();
    const parsedYear = Number(this.form.year);
    const year = Number.isFinite(parsedYear) && parsedYear > 0 ? Math.trunc(parsedYear) : undefined;
    const normalizedVin = vin.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

    if (!vin && (!make || !model)) {
      this.errorMessage = 'Provide VIN or both make and model to match a vehicle profile.';
      return;
    }

    if (vin && normalizedVin.length < 11) {
      this.errorMessage = 'VIN must contain at least 11 letters/numbers.';
      return;
    }

    this.matchingVehicle = true;
    this.errorMessage = '';
    this.vehicleMatchMessage = '';
    this.startMatchUiGuard();

    this.vehicleProfilesApi
      .matchVehicle({
        vin: vin || undefined,
        make: make || undefined,
        model: model || undefined,
        year
      })
      .pipe(
        timeout(this.vinMatchTimeoutMs),
        finalize(() => {
          this.clearMatchUiGuard();
          this.matchingVehicle = false;
          this.syncUi();
        })
      )
      .subscribe({
        next: (match) => {
          this.applyVehicleMatch(match);
        },
        error: (error: unknown) => {
          this.errorMessage = this.getApiError(error, 'No vehicle match found for the provided input.');
        }
      });
  }

  loadVehicles(preferredVehicleId: number | null = null): void {
    this.loading = true;
    this.errorMessage = '';
    this.startLoadGuard();
    this.syncUi();

    this.vehicleProfilesApi
      .getByOwner(this.ownerId)
      .pipe(
        timeout(this.requestTimeoutMs),
        retry({
          count: 1,
          delay: 500
        })
      )
      .subscribe({
        next: (vehicles) => {
          try {
            const normalizedVehicles = Array.isArray(vehicles) ? vehicles : [];
            this.vehicles = normalizedVehicles;

            if (this.vehicles.length === 0) {
              this.selectedVehicleId = null;
              this.persistSelectedVehicleId(null);
              return;
            }

            if (preferredVehicleId !== null && this.vehicles.some((vehicle) => vehicle.id === preferredVehicleId)) {
              this.selectedVehicleId = preferredVehicleId;
            } else if (
              this.selectedVehicleId !== null &&
              this.vehicles.some((vehicle) => vehicle.id === this.selectedVehicleId)
            ) {
              // Keep previously selected vehicle when refreshing list.
            } else {
              this.selectedVehicleId = this.vehicles[0].id;
            }

            this.persistSelectedVehicleId(this.selectedVehicleId);
          } catch {
            this.vehicles = [];
            this.selectedVehicleId = null;
            this.errorMessage = 'Unexpected response while loading vehicle profiles.';
          } finally {
            this.stopLoadingState();
          }
        },
        error: (error: unknown) => {
          this.errorMessage = this.getApiError(error, 'Unable to load vehicle profiles.');
          this.stopLoadingState();
        }
      });
  }

  selectVehicle(vehicleId: number): void {
    this.selectedVehicleId = vehicleId;
    this.persistSelectedVehicleId(vehicleId);
    this.errorMessage = '';
  }

  continueToDashboard(): void {
    if (this.selectedVehicleId === null) {
      this.errorMessage = 'Select a vehicle before continuing to dashboard.';
      return;
    }

    this.persistSelectedVehicleId(this.selectedVehicleId);
    this.router.navigate(['/dashboard'], {
      queryParams: {
        ownerId: this.ownerId,
        vehicleId: this.selectedVehicleId
      }
    });
  }

  addVehicle(): void {
    const vin = this.form.vin.trim();
    const make = this.form.make.trim();
    const model = this.form.model.trim();
    const year = this.parseStrictInteger(this.form.year);
    const currentOdometerKm = this.parseStrictInteger(this.form.currentOdometerKm);

    if (!vin && (!make || !model)) {
      this.errorMessage = 'Enter VIN or provide both make and model.';
      return;
    }

    if (!vin) {
      if (year === null) {
        this.errorMessage = 'Year must be an integer.';
        return;
      }

      if (year < this.minVehicleYear || year > this.maxVehicleYear) {
        this.errorMessage = `Year must be between ${this.minVehicleYear} and ${this.maxVehicleYear}.`;
        return;
      }
    }

    if (currentOdometerKm === null) {
      this.errorMessage = 'Mileage must be an integer.';
      return;
    }

    if (!Number.isInteger(currentOdometerKm)) {
      this.errorMessage = 'Mileage must be an integer.';
      return;
    }

    const initialPartMileage = this.buildInitialPartMileage(Math.trunc(currentOdometerKm));
    if (initialPartMileage === null) {
      return;
    }

    const payload: CreateVehicleProfileRequest = {
      ownerId: this.ownerId,
      make,
      model,
      year: year !== null && Number.isFinite(year) ? Math.trunc(year) : 0,
      currentOdometerKm: Math.trunc(currentOdometerKm),
      vin: vin || undefined,
      initialPartMileage
    };

    this.saving = true;
    this.errorMessage = '';
    this.vehicleMatchMessage = '';

    this.vehicleProfilesApi
      .create(payload)
      .pipe(
        timeout(this.requestTimeoutMs),
        finalize(() => {
          this.saving = false;
          this.syncUi();
        })
      )
      .subscribe({
        next: (createdVehicle) => {
          const createdVehicleId =
            createdVehicle && typeof createdVehicle.id === 'number' && Number.isFinite(createdVehicle.id)
              ? createdVehicle.id
              : null;

          this.viewMode = 'profiles';
          this.selectedMakeCandidate = '';
          this.selectedModelCandidate = null;
          this.resetAddFormValues();
          this.vehicleMatchMessage = '';
          this.errorMessage = '';

          if (createdVehicleId !== null) {
            this.selectedVehicleId = createdVehicleId;
            this.persistSelectedVehicleId(createdVehicleId);
          }

          // Always refresh from backend after create so UI state is consistent even when
          // response payload shape differs across environments.
          this.loadVehicles(createdVehicleId ?? this.selectedVehicleId);
          this.syncUi();
        },
        error: (error: unknown) => {
          if (this.isInitialPartHistoryFailure(error)) {
            this.viewMode = 'profiles';
            this.selectedMakeCandidate = '';
            this.selectedModelCandidate = null;
            this.resetAddFormValues();
            this.vehicleMatchMessage = '';
            this.errorMessage = '';
            this.loadVehicles(this.selectedVehicleId);
            this.syncUi();
            return;
          }

          this.errorMessage = this.getApiError(error, 'Unable to create vehicle profile.');
          this.syncUi();
        }
      });
  }

  deleteSelectedVehicle(): void {
    if (this.selectedVehicleId === null) {
      this.errorMessage = 'Select a vehicle to delete.';
      return;
    }

    if (!window.confirm('Delete the selected vehicle profile?')) {
      return;
    }

    const deletingVehicleId = this.selectedVehicleId;
    const previousVehicles = [...this.vehicles];
    const previousSelectedVehicleId = this.selectedVehicleId;

    this.vehicles = this.vehicles.filter((vehicle) => vehicle.id !== deletingVehicleId);
    this.selectedVehicleId = this.vehicles.length > 0 ? this.vehicles[0].id : null;
    this.persistSelectedVehicleId(this.selectedVehicleId);

    this.deleting = true;
    this.errorMessage = '';
    this.syncUi();

    this.vehicleProfilesApi
      .delete(deletingVehicleId, this.ownerId)
      .pipe(
        timeout(this.requestTimeoutMs),
        finalize(() => {
          this.deleting = false;
        })
      )
      .subscribe({
        next: () => {},
        error: (error: unknown) => {
          this.vehicles = previousVehicles;
          this.selectedVehicleId = previousSelectedVehicleId;
          this.persistSelectedVehicleId(this.selectedVehicleId);
          this.errorMessage = this.getApiError(error, 'Unable to delete selected vehicle.');
          this.syncUi();
        }
      });
  }

  logout(): void {
    this.clearSavedSession();
    this.router.navigate(['/login']);
  }

  private getApiError(error: unknown, fallbackMessage: string): string {
    if (error instanceof TimeoutError) {
      return 'Request timed out. Backend may be slow or unavailable. Please try again.';
    }

    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return `Cannot reach backend API at ${API_BASE_URL}.`;
      }

      if (typeof error.error === 'string' && error.error.trim().length > 0) {
        return error.error;
      }

      if (error.error?.message && typeof error.error.message === 'string') {
        return error.error.message;
      }

      if (error.error?.detail && typeof error.error.detail === 'string') {
        return error.error.detail;
      }

      if (error.error?.title && typeof error.error.title === 'string') {
        return error.error.title;
      }

      if (error.error?.errors && typeof error.error.errors === 'object') {
        const validationErrors = Object.values(error.error.errors)
          .flatMap((item: unknown) => (Array.isArray(item) ? item : [item]))
          .filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0);

        if (validationErrors.length > 0) {
          return validationErrors[0];
        }
      }
    }

    return fallbackMessage;
  }

  private isInitialPartHistoryFailure(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    const detail =
      (typeof error.error === 'string' && error.error) ||
      (typeof error.error?.detail === 'string' && error.error.detail) ||
      (typeof error.error?.message === 'string' && error.error.message) ||
      (typeof error.error?.title === 'string' && error.error.title) ||
      '';

    return detail.includes('Vehicle created, but failed to initialize part durability history.');
  }

  private buildInitialPartMileage(currentOdometerKm: number): InitialPartMileageRequest[] | null {
    const partValues: Array<{ partName: string; value: number | null }> = [
      { partName: 'Oil Filter', value: this.parseStrictInteger(this.form.oilFilterKm) },
      { partName: 'Engine Oil', value: this.parseStrictInteger(this.form.engineOilKm) },
      { partName: 'Spark Plugs', value: this.parseStrictInteger(this.form.sparkPlugsKm) },
      { partName: 'Brake Pads', value: this.parseStrictInteger(this.form.brakePadsKm) },
      { partName: 'Cabin Air Filter', value: this.parseStrictInteger(this.form.cabinAirFilterKm) },
      { partName: 'Engine Air Filter', value: this.parseStrictInteger(this.form.engineAirFilterKm) },
      { partName: 'Fuel Filter', value: this.parseStrictInteger(this.form.fuelFilterKm) }
    ];

    const result: InitialPartMileageRequest[] = [];
    for (const part of partValues) {
      if (part.value === null) {
        this.errorMessage = `Initial mileage for ${part.partName} must be an integer.`;
        return null;
      }

      if (!Number.isInteger(part.value)) {
        this.errorMessage = `Initial mileage for ${part.partName} must be an integer.`;
        return null;
      }

      const mileage = Math.trunc(part.value);
      if (mileage < 0) {
        this.errorMessage = `Initial mileage for ${part.partName} must be zero or greater.`;
        return null;
      }

      if (mileage > currentOdometerKm) {
        this.errorMessage = `Initial mileage for ${part.partName} cannot exceed vehicle mileage.`;
        return null;
      }

      if (mileage > 0) {
        result.push({
          partName: part.partName,
          odometerKm: mileage
        });
      }
    }

    return result;
  }

  private parseStrictInteger(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isInteger(value) ? value : null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!/^\d+$/.test(trimmed)) {
        return null;
      }

      const parsed = Number.parseInt(trimmed, 10);
      return Number.isInteger(parsed) ? parsed : null;
    }

    return null;
  }

  private applyVehicleMatch(match: VehicleCatalogMatchResponse): void {
    const catalogMake = this.resolveCatalogMake(match.make);
    this.form.make = catalogMake ?? match.make;
    this.form.model = match.model;
    if (match.year !== undefined && Number.isFinite(match.year)) {
      this.form.year = Math.trunc(match.year);
    }

    this.viewMode = 'add';
    this.selectedMakeCandidate = this.form.make;
    this.selectedModelCandidate = this.findModelOption(this.form.make, this.form.model, this.form.year);

    const matchReason = match.matchedByVinPattern ? 'VIN pattern' : 'profile';
    // VIN match method
    //this.vehicleMatchMessage = `Matched ${match.vehicleName} via ${matchReason} (${match.matchSource}).`;
  }

  vehicleImageFor(make: string | null | undefined, model: string | null | undefined): string {
    const option = this.findModelOption(make, model);
    if (option !== null) {
      return `assets/vehicles/${option.imageType}.svg`;
    }

    const normalizedMake = (make ?? '').toLowerCase();
    const normalizedModel = (model ?? '').toLowerCase();

    if (
      normalizedModel.includes('cr-v') ||
      normalizedModel.includes('rav4') ||
      normalizedModel.includes('rogue') ||
      normalizedModel.includes('outback') ||
      normalizedModel.includes('sorento') ||
      normalizedModel.includes('highlander') ||
      normalizedModel.includes('model y') ||
      normalizedModel.includes('explorer') ||
      normalizedModel.includes('equinox') ||
      normalizedModel.includes('cherokee') ||
      normalizedModel.includes('rx')
    ) {
      return 'assets/vehicles/suv.svg';
    }

    if (
      normalizedModel.includes('golf') ||
      normalizedModel.includes('civic') ||
      normalizedModel.includes('corolla') ||
      normalizedModel.includes('elantra') ||
      normalizedModel.includes('model 3')
    ) {
      return 'assets/vehicles/hatch.svg';
    }

    if (normalizedMake.includes('tesla') && normalizedModel.includes('3')) {
      return 'assets/vehicles/hatch.svg';
    }

    return 'assets/vehicles/sedan.svg';
  }

  get addStep(): 'make' | 'model' | 'details' {
    if (this.form.make.trim().length === 0) {
      return 'make';
    }

    if (this.form.model.trim().length === 0) {
      return 'model';
    }

    return 'details';
  }

  get modelOptionsForSelectedMake(): VehicleModelOption[] {
    const selectedMake = this.resolveCatalogMake(this.form.make);
    if (selectedMake === null) {
      return [];
    }

    return this.makeModelCatalog[selectedMake];
  }

  get addVehicleTitle(): string {
    const parts = [this.form.year > 0 ? this.form.year.toString() : '', this.form.make, this.form.model]
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    if (parts.length === 0) {
      return 'Vehicle Profile Preview';
    }

    return parts.join(' ');
  }

  get addVehicleSubtitle(): string {
    if (this.selectedModelCandidate?.trim && this.selectedModelCandidate.trim.trim().length > 0) {
      return this.selectedModelCandidate.trim;
    }

    return 'Vehicle Profile';
  }

  private findModelOption(
    make: string | null | undefined,
    model: string | null | undefined,
    year?: number
  ): VehicleModelOption | null {
    const catalogMake = this.resolveCatalogMake(make);
    const normalizedModel = (model ?? '').trim().toLowerCase();
    if (catalogMake === null || normalizedModel.length === 0) {
      return null;
    }

    const options = this.makeModelCatalog[catalogMake];
    const byModel = options.find((option) => option.model.trim().toLowerCase() === normalizedModel);
    if (byModel === undefined) {
      return null;
    }

    if (year !== undefined && Number.isFinite(year)) {
      const byYear = options.find(
        (option) => option.model.trim().toLowerCase() === normalizedModel && option.year === Math.trunc(year)
      );
      return byYear ?? byModel;
    }

    return byModel;
  }

  private resolveCatalogMake(make: string | null | undefined): string | null {
    const normalizedMake = (make ?? '').trim().toLowerCase();
    if (normalizedMake.length === 0) {
      return null;
    }

    for (const catalogMake of Object.keys(this.makeModelCatalog)) {
      if (catalogMake.trim().toLowerCase() === normalizedMake) {
        return catalogMake;
      }
    }

    return null;
  }

  private resetAddFormValues(): void {
    this.form.vin = '';
    this.form.make = '';
    this.form.model = '';
    this.form.year = new Date().getFullYear();
    this.form.currentOdometerKm = 0;
    this.form.oilFilterKm = 0;
    this.form.engineOilKm = 0;
    this.form.sparkPlugsKm = 0;
    this.form.brakePadsKm = 0;
    this.form.cabinAirFilterKm = 0;
    this.form.engineAirFilterKm = 0;
    this.form.fuelFilterKm = 0;
  }

  private startLoadGuard(): void {
    this.clearLoadGuard();
    this.loadGuardTimer = setTimeout(() => {
      this.ngZone.run(() => {
        if (!this.loading) {
          return;
        }

        this.loading = false;
        if (!this.errorMessage) {
          this.errorMessage = 'Loading vehicle profiles timed out. Click Retry Load.';
        }

        this.syncUi();
      });
    }, this.requestTimeoutMs + this.loadGuardPaddingMs);
  }

  private stopLoadingState(): void {
    this.clearLoadGuard();
    this.loading = false;
    this.syncUi();
  }

  private clearLoadGuard(): void {
    if (this.loadGuardTimer !== null) {
      clearTimeout(this.loadGuardTimer);
      this.loadGuardTimer = null;
    }
  }

  private startMatchUiGuard(): void {
    this.clearMatchUiGuard();
    this.matchUiGuardTimer = setTimeout(() => {
      this.ngZone.run(() => {
        if (!this.matchingVehicle) {
          return;
        }

        this.matchingVehicle = false;
        if (!this.errorMessage) {
          this.errorMessage = 'VIN match timed out. Please retry.';
        }

        this.syncUi();
      });
    }, this.vinMatchTimeoutMs + this.matchUiGuardPaddingMs);
  }

  private clearMatchUiGuard(): void {
    if (this.matchUiGuardTimer !== null) {
      clearTimeout(this.matchUiGuardTimer);
      this.matchUiGuardTimer = null;
    }
  }

  private syncUi(): void {
    if (this.destroyed) {
      return;
    }

    this.cdr.detectChanges();
  }

  private getSavedOwnerId(): string | null {
    try {
      const stored = localStorage.getItem('autoInsight.ownerId');
      if (!stored) {
        return null;
      }

      const trimmed = stored.trim();
      return trimmed.length > 0 ? trimmed : null;
    } catch {
      return null;
    }
  }

  private persistOwnerId(ownerId: string): void {
    try {
      localStorage.setItem('autoInsight.ownerId', ownerId);
    } catch {
      // Ignore local storage failures in restricted contexts.
    }
  }

  private persistSelectedVehicleId(vehicleId: number | null): void {
    try {
      if (vehicleId === null) {
        localStorage.removeItem('autoInsight.vehicleId');
        return;
      }

      localStorage.setItem('autoInsight.vehicleId', vehicleId.toString());
    } catch {
      // Ignore local storage failures in restricted contexts.
    }
  }

  private clearSavedSession(): void {
    try {
      localStorage.removeItem('autoInsight.username');
      localStorage.removeItem('autoInsight.role');
      localStorage.removeItem('autoInsight.ownerId');
      localStorage.removeItem('autoInsight.vehicleId');
    } catch {
      // Ignore local storage failures in restricted contexts.
    }
  }
}
